import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { usePreviousDistinct, useToggle } from 'react-use';
import { toast } from 'react-toastify';
import { isSameMonth } from 'date-fns';
import { SchedulerMutationActions, WorkdaySyncHookData } from '@lp/features/Scheduler/types';
import { getPlanningRefetchTime, getResourceEmptyShift, withoutTzOffset } from '@lp/features/Scheduler/utils';
import { useScheduleMutationsLoader } from '@lp/features/Scheduler/hooks';
import { BaseSchedulerHighlightValue } from '@lp/core/store/scheduler';
import { EmployeeRole, EmployeesReservationsData } from '@/api/apis/resources';
import {
  BaseResource,
  BaseShift,
  DateRangeParams,
  MonthSchedule,
  PlanningProcess,
  PlanningProcessStatus,
  ResetShiftTypeRequestBody,
  ResolveHrSystemConflict,
  ResourceShiftTypeHrSystemConflict,
  ResourcesWithShifts,
  SetFixationTypeRequestBody,
  ShiftFixationType,
  ShiftStatus,
  ShiftType,
} from '@/api/apis/resources/schedule';
import { retrieveUnifiedError, withRequestErrorHandler } from '@/api/utils';
import { useEmployeesReservations } from '@/queries/resource';
import { getFullName } from '@/utils/helpers';

type SchedulerDataType<R extends BaseResource, S extends BaseShift<string>> = {
  monthSchedule?: MonthSchedule;
  planningData?: PlanningProcess;
  shiftsData?: ResourcesWithShifts<R, S>;
  reservationsData?: EmployeesReservationsData['employees'];
  isShiftsLoading: boolean;
  isPlanningMode: boolean;
  togglePlanningMode: (nextValue?: boolean) => void;
};

type CreateSchedulerDataHookArgs<R extends BaseResource, S extends BaseShift<string>> = {
  useMonthScheduleQuery(startDayOfMonth: Date, refetchTime?: number): UseQueryResult<MonthSchedule>;
  usePlanningProcessQuery(
    processId?: string | null,
    enabled?: boolean,
    refetchTime?: number,
  ): UseQueryResult<PlanningProcess>;
  useScheduleShiftsQuery(
    dateRange: DateRangeParams,
    processId?: string | null,
    isPlanned?: boolean,
  ): UseQueryResult<ResourcesWithShifts<R, S>>;
  employeeRole?: EmployeeRole;
};

type SchedulerDataHookArgs = {
  startDay: Date;
  dateRange: DateRangeParams;
  isReservationsView?: boolean;
  forcePlanningDisable?: boolean;
  isPlanningInitial?: boolean;
};

type CreateSchedulerDataHookType = <R extends BaseResource, S extends BaseShift<string>>(
  args: CreateSchedulerDataHookArgs<R, S>,
) => (args: SchedulerDataHookArgs) => SchedulerDataType<R, S>;

export const createSchedulerDataHook: CreateSchedulerDataHookType =
  ({ useMonthScheduleQuery, usePlanningProcessQuery, useScheduleShiftsQuery, employeeRole }) =>
  ({ startDay, dateRange, isReservationsView, forcePlanningDisable, isPlanningInitial }) => {
    const [planningEnabled, togglePlanningMode] = useToggle(!!isPlanningInitial);
    const [refetchTime, setRefetchTime] = useState<number>();
    const apiStartDay = useMemo(() => withoutTzOffset(startDay), [startDay]);
    const apiDateRange = useMemo(() => {
      return {
        from: withoutTzOffset(dateRange.from),
        to: withoutTzOffset(dateRange.to),
      };
    }, [dateRange]);
    const reservationsDataParams = { ...apiDateRange, employeeRoles: employeeRole ? [employeeRole] : [] };

    const [isMutating] = useScheduleMutationsLoader();
    const { data: monthSchedule } = useMonthScheduleQuery(apiStartDay, refetchTime);
    const { planningProcessId, planningProcessStatus } = monthSchedule ?? {};
    const isProcessing = planningProcessStatus === PlanningProcessStatus.Processing;
    const processId = planningProcessId;
    const isPlanningMode =
      !monthSchedule?.scheduleId && !!processId && planningEnabled && !isReservationsView && !forcePlanningDisable;
    const { data: planningData } = usePlanningProcessQuery(processId, isPlanningMode, refetchTime);
    const reservationsQuery = useEmployeesReservations(reservationsDataParams, isReservationsView);
    const previousPlanningStatus = usePreviousDistinct(planningData?.status);
    const {
      data: shiftsData,
      isFetching,
      isPlaceholderData,
      refetch: refetchShiftsData,
    } = useScheduleShiftsQuery(apiDateRange, processId, isPlanningMode);

    const isReservationsFetching = reservationsQuery.isFetching && reservationsQuery.isPlaceholderData;
    const isShiftsLoading = (isFetching && isPlaceholderData) || isProcessing || isMutating || isReservationsFetching;

    useEffect(() => {
      const ms = isPlanningMode ? getPlanningRefetchTime(planningProcessStatus, planningData) : undefined;
      setRefetchTime(ms);
    }, [isPlanningMode, planningProcessStatus, planningData, setRefetchTime]);

    useEffect(() => {
      const wasGenerating = previousPlanningStatus === PlanningProcessStatus.Generating;
      const isCurrentlyEditing = planningData?.status === PlanningProcessStatus.Editing;

      if (wasGenerating && isCurrentlyEditing) refetchShiftsData();
    }, [previousPlanningStatus, planningData?.status, refetchShiftsData]);

    return {
      monthSchedule,
      isPlanningMode,
      planningData,
      shiftsData,
      reservationsData: reservationsQuery.data?.employees,
      isShiftsLoading,
      togglePlanningMode,
    };
  };

type CreateSchedulerMutationActionsHookArgs = {
  useCreatePlanningMutation(): UseMutationResult<PlanningProcess, Error, Date>;
  useRegeneratePlanningMutation(startDayOfMonth: Date): UseMutationResult<PlanningProcess, Error, string>;
  useCancelPlanningMutation(startDayOfMonth: Date): UseMutationResult<PlanningProcess, Error, string>;
  usePublishPlanningMutation(
    startDayOfMonth: Date,
  ): UseMutationResult<PlanningProcess, Error, { processId: string; scheduleId: string }>;
  useCancelGenerationPlanningMutation(
    startDayOfMonth: Date,
  ): UseMutationResult<PlanningProcess, Error, { processId: string; jobId: string }>;
};

type CreateSchedulerMutationActionsHookType = (
  args: CreateSchedulerMutationActionsHookArgs,
) => (startDay: Date, planningData?: PlanningProcess) => SchedulerMutationActions;

export const createSchedulerMutationActionsHook: CreateSchedulerMutationActionsHookType =
  ({
    useCreatePlanningMutation,
    useRegeneratePlanningMutation,
    useCancelPlanningMutation,
    usePublishPlanningMutation,
    useCancelGenerationPlanningMutation,
  }) =>
  (startDay: Date, planningData?: PlanningProcess) => {
    const apiStartDay = useMemo(() => withoutTzOffset(startDay), [startDay]);
    const { mutateAsync: createPlanningProcess, isPending: createPending } = useCreatePlanningMutation();
    const { mutateAsync: startGeneration, isPending: regPending } = useRegeneratePlanningMutation(apiStartDay);
    const { mutateAsync: discardPlanningProcess, isPending: discardPending } = useCancelPlanningMutation(apiStartDay);
    const { mutateAsync: publishPlanningProcess, isPending: publishPending } = usePublishPlanningMutation(apiStartDay);
    const { mutateAsync: cancelGenerationProcess, isPending: cancelPending } =
      useCancelGenerationPlanningMutation(apiStartDay);
    const [, setIsMutating] = useScheduleMutationsLoader();

    const isMutating = useMemo(() => {
      return createPending || regPending || discardPending || publishPending || cancelPending;
    }, [createPending, regPending, discardPending, publishPending, cancelPending]);

    useEffect(() => {
      setIsMutating(isMutating);
    }, [isMutating, setIsMutating]);

    const onPlanningStart = useCallback(async () => {
      if (planningData) return;
      await withRequestErrorHandler(() => createPlanningProcess(apiStartDay));
    }, [apiStartDay, planningData, createPlanningProcess]);

    const onGenerationStart = useCallback(async () => {
      if (!planningData?.id) return;
      await withRequestErrorHandler(() => startGeneration(planningData.id));
    }, [planningData?.id, startGeneration]);

    const onPlanningDiscard = useCallback(async () => {
      if (!planningData?.id) return;
      await withRequestErrorHandler(() => discardPlanningProcess(planningData.id));
    }, [planningData?.id, discardPlanningProcess]);

    const onPlanningPublish = useCallback(async () => {
      if (!planningData?.id || !planningData.scheduleId) return;
      const { id: processId, scheduleId } = planningData;
      await withRequestErrorHandler(() => publishPlanningProcess({ processId, scheduleId }));
    }, [planningData, publishPlanningProcess]);

    const onGenerationCancel = useCallback(async () => {
      if (!planningData?.id || !planningData?.jobId) return;
      const { id: processId, jobId } = planningData;
      await withRequestErrorHandler(() => cancelGenerationProcess({ processId, jobId }));
    }, [planningData, cancelGenerationProcess]);

    return {
      isMutating,
      onPlanningStart,
      onGenerationStart,
      onPlanningDiscard,
      onPlanningPublish,
      onGenerationCancel,
    };
  };

type CreateWorkdaySyncHookArgs<R extends BaseResource, T extends ShiftType, P extends Record<string, unknown>> = {
  useWorkdaySyncMutation(
    startDayOfMonth: Date,
    dateRange: DateRangeParams,
    processId?: string | null,
    isPlanned?: boolean,
  ): UseMutationResult<null, Error, string>;
  useGetWorkdayConflicts(
    scheduleId?: string,
    enabled?: boolean,
  ): UseQueryResult<ResourceShiftTypeHrSystemConflict<R, T>[]>;
  useHrSystemConflictResolveMutation(
    dateRange: DateRangeParams,
    scheduleId?: string,
  ): UseMutationResult<unknown, Error, ResolveHrSystemConflict<P>>;
};

type CreateWorkdaySyncHookType = <R extends BaseResource, T extends ShiftType, P extends Record<string, unknown>>(
  args: CreateWorkdaySyncHookArgs<R, T, P>,
) => (
  startDayOfMonth: Date,
  dateRange: DateRangeParams,
  monthSchedule?: MonthSchedule,
  isPlanningMode?: boolean,
  readonly?: boolean,
) => WorkdaySyncHookData<R, T, P>;

export const createWorkdaySyncHook: CreateWorkdaySyncHookType =
  ({ useWorkdaySyncMutation, useGetWorkdayConflicts, useHrSystemConflictResolveMutation }) =>
  (startDayOfMonth, dateRange, monthSchedule, isPlanningMode, readonly) => {
    const apiDateRange = useMemo(() => {
      return {
        from: withoutTzOffset(dateRange.from),
        to: withoutTzOffset(dateRange.to),
      };
    }, [dateRange]);
    const { planningProcessId } = monthSchedule ?? {};
    const { mutateAsync, isPending } = useWorkdaySyncMutation(
      startDayOfMonth,
      apiDateRange,
      planningProcessId,
      isPlanningMode,
    );
    const isPublished = !!monthSchedule?.hasSchedule && !!monthSchedule.scheduleId;
    const scheduleId = isPlanningMode
      ? monthSchedule?.inPlanningScheduleId
      : (monthSchedule?.scheduleId ?? monthSchedule?.preScheduleId);
    const { data: workdayConflicts, isFetching: isConflictsFetching } = useGetWorkdayConflicts(
      scheduleId ?? undefined,
      isPublished && !readonly,
    );
    const { mutateAsync: resolveHrSystemConflict, isPending: isConflictResolvePending } =
      useHrSystemConflictResolveMutation(apiDateRange, monthSchedule?.scheduleId ?? undefined);
    const lastScheduleSyncTime = isPublished
      ? monthSchedule?.scheduleHrSystemLastSyncDate
      : monthSchedule?.preScheduleHrSystemLastSyncDate;
    const lastSyncTime = isPlanningMode ? monthSchedule?.inPlanningScheduleHrSystemLastSyncDate : lastScheduleSyncTime;
    const isDisabled = isPending || !scheduleId;
    const isConflictsLoading = isConflictResolvePending || isConflictsFetching;

    const onWorkdaySync = useCallback(async () => {
      try {
        if (scheduleId) {
          await mutateAsync(scheduleId);
          toast.success('Workday successfully synced');
        }
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    }, [mutateAsync, scheduleId]);

    const onWorkdayConflictResolve = useCallback(
      async (payload: Parameters<typeof resolveHrSystemConflict>['0']) => {
        try {
          await resolveHrSystemConflict(payload);
          toast.success('Workday conflict successfully resolved');
        } catch (e) {
          const error = retrieveUnifiedError(e);
          toast.error(error.message);
        }
      },
      [resolveHrSystemConflict],
    );

    return {
      isDisabled,
      lastSyncTime,
      workdayConflicts,
      isConflictsLoading,
      onWorkdaySync,
      onWorkdayConflictResolve,
    };
  };

type ShiftRequestBaseFields<P> = P & {
  scheduleId: string;
  resourceId: string;
  day: string;
  shiftStatus: ShiftStatus;
  isEditable: boolean;
  isPreSchedule?: boolean;
};

type CreateUseShiftChangeHookArgs<
  TPayload,
  TRequestBody extends ShiftRequestBaseFields<TPayload>,
  TShift,
  TResourcesWithShifts extends ResourcesWithShifts<BaseResource, BaseShift<string>>,
> = {
  useScheduleSetShiftTypeOptimisticMutation(
    dateRange: DateRangeParams,
    processId?: string | null,
    isPlanned?: boolean,
  ): UseMutationResult<TShift, Error, TRequestBody, TResourcesWithShifts | undefined>;
  useScheduleResetShiftTypeOptimisticMutation(
    dateRange: DateRangeParams,
    processId?: string | null,
    isPlanned?: boolean,
  ): UseMutationResult<null, Error, ResetShiftTypeRequestBody, TResourcesWithShifts | undefined>;
};

type UseShiftChangeReturnType<TPayload> = (resourceId: string, day: string, payload?: TPayload) => Promise<void>;

type UseShiftChangeHookType<TPayload> = (
  dateRange: DateRangeParams,
  monthSchedule?: MonthSchedule,
  isPlanningMode?: boolean,
) => UseShiftChangeReturnType<TPayload>;

type CreateUseShiftChangeHookType = <
  TPayload,
  TRequestBody extends ShiftRequestBaseFields<TPayload>,
  TShift,
  TResourcesWithShifts extends ResourcesWithShifts<BaseResource, BaseShift<string>>,
>(
  args: CreateUseShiftChangeHookArgs<TPayload, TRequestBody, TShift, TResourcesWithShifts>,
) => UseShiftChangeHookType<TPayload>;

export const createUseShiftChangeHook: CreateUseShiftChangeHookType =
  ({ useScheduleSetShiftTypeOptimisticMutation, useScheduleResetShiftTypeOptimisticMutation }) =>
  (dateRange, monthSchedule, isPlanningMode = false) => {
    const processId = monthSchedule?.planningProcessId;
    const apiDateRange = useMemo(() => {
      return {
        from: withoutTzOffset(dateRange.from),
        to: withoutTzOffset(dateRange.to),
      };
    }, [dateRange]);

    const { mutateAsync: setShiftType } = useScheduleSetShiftTypeOptimisticMutation(
      apiDateRange,
      processId,
      isPlanningMode,
    );
    const { mutateAsync: resetShiftType } = useScheduleResetShiftTypeOptimisticMutation(
      apiDateRange,
      processId,
      isPlanningMode,
    );

    return useCallback(
      async (resourceId, day, payload) => {
        try {
          const isPreSchedule = !isPlanningMode && !monthSchedule?.scheduleId && !!monthSchedule?.preScheduleId;
          const scheduleId = isPlanningMode
            ? monthSchedule?.inPlanningScheduleId
            : (monthSchedule?.scheduleId ?? monthSchedule?.preScheduleId);

          if (scheduleId) {
            if (payload) {
              const shiftStatus = isPlanningMode ? ShiftStatus.InPlanningProcess : ShiftStatus.Actual;
              await setShiftType({
                ...payload,
                scheduleId,
                resourceId,
                day,
                shiftStatus,
                isEditable: true,
                isPreSchedule,
              } as Parameters<typeof setShiftType>[0]);
            } else {
              await resetShiftType({ scheduleId, resourceId, day });
            }
          }
        } catch (e) {
          const error = retrieveUnifiedError(e);
          toast.error(error.message);
        }
      },
      [isPlanningMode, monthSchedule, setShiftType, resetShiftType],
    );
  };

type CreateUseUnblockShiftHookArgs = {
  useSetFixationTypeOptimisticMutation(
    dateRange: DateRangeParams,
    processId?: string | null,
    isPlanned?: boolean,
  ): UseMutationResult<null, Error, SetFixationTypeRequestBody, unknown>;
};

type UseUnblockShiftReturnType = (resourceId: string, day: string, scheduleId: string) => Promise<void>;

type UseUnblockShiftHookType = (
  dateRange: DateRangeParams,
  monthSchedule?: MonthSchedule,
  isPlanningMode?: boolean,
) => UseUnblockShiftReturnType;

export const createUseUnblockShiftHook =
  ({ useSetFixationTypeOptimisticMutation }: CreateUseUnblockShiftHookArgs): UseUnblockShiftHookType =>
  (dateRange, monthSchedule, isPlanningMode = false) => {
    const processId = monthSchedule?.planningProcessId;
    const apiDateRange = useMemo(() => {
      return {
        from: withoutTzOffset(dateRange.from),
        to: withoutTzOffset(dateRange.to),
      };
    }, [dateRange]);

    const { mutateAsync: setFixationType } = useSetFixationTypeOptimisticMutation(
      apiDateRange,
      processId,
      isPlanningMode,
    );

    return useCallback(
      async (resourceId: string, day: string, scheduleId: string) => {
        try {
          await setFixationType({
            scheduleId,
            resourceId,
            day,
            fixationType: ShiftFixationType.Assigned,
          });
        } catch (e) {
          const error = retrieveUnifiedError(e);
          toast.error(error.message);
        }
      },
      [setFixationType],
    );
  };

type CreateSchedulerShiftHighlightHookArgs<T> = {
  useSetHighlightValue(): (value: T) => void;
};

type UseSchedulerShiftHighlightProps<
  R extends BaseResource,
  S extends BaseShift<string>,
  TResourcesWithShifts extends ResourcesWithShifts<R, S>,
> = {
  selectedDate: Date;
  daysInterval: Date[];
  shiftsData?: TResourcesWithShifts;
};

export const createSchedulerShiftHighlightHook =
  <
    T extends BaseSchedulerHighlightValue,
    R extends BaseResource,
    S extends BaseShift<string>,
    TResourcesWithShifts extends ResourcesWithShifts<R, S>,
  >({
    useSetHighlightValue,
  }: CreateSchedulerShiftHighlightHookArgs<T>) =>
  ({ selectedDate, daysInterval, shiftsData }: UseSchedulerShiftHighlightProps<R, S, TResourcesWithShifts>) => {
    const setHighlightValue = useSetHighlightValue();

    const highlightResourceEmptyShift = useCallback(
      (resourceId: string) => {
        if (!shiftsData) return;
        const currentMonthDays = daysInterval.filter((d) => isSameMonth(d, selectedDate));
        const obj = getResourceEmptyShift(resourceId, currentMonthDays, shiftsData) as unknown as T;

        if (obj && obj.resourceId) {
          setHighlightValue(obj);
        } else {
          const resource = shiftsData[resourceId]?.resource;
          const fullName = getFullName(resource?.firstName, resource?.lastName);
          toast.warning(`${fullName} cannot be displayed in the schedule`);
        }
      },
      [daysInterval, selectedDate, setHighlightValue, shiftsData],
    );

    const highlightByViolation = useCallback(
      (data: T) => {
        if (data.resourceId || data.day) {
          setHighlightValue({ resourceId: data.resourceId, day: data.day } as T);
        }
      },
      [setHighlightValue],
    );

    return { highlightByViolation, highlightResourceEmptyShift };
  };
